import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, Subscription, take } from 'rxjs';
import { KbDataExtractionRequestService } from 'src/app/services/feature/data-extraction-request/kb-data-extraction-request.service';
import { RegisterService } from 'src/app/services/feature/register.service';

@Component({
  selector: 'app-kb-incident-information',
  templateUrl: './kb-incident-information.component.html',
  styleUrl: './kb-incident-information.component.scss',
  standalone: false
})
export class KbIncidentInformationComponent {

  private fb = inject(FormBuilder);
  private registerService = inject(RegisterService);
  private cdr = inject(ChangeDetectorRef);
  private dataExtractionService = inject(KbDataExtractionRequestService);
  private datePipe = inject(DatePipe);

  pageFieldData: any;
  pageFieldData$ = this.registerService.getRegisterPageFields();
  accessRequestsWSList: any;
  countriesWSList: any;
  stateList: any;

  allowNext: boolean = false;
  subscription = new Subscription();
  private timeout?: ReturnType<typeof setTimeout> | null = null;// Store the timeout ID
  incidentform = this.fb.group({
    accidentResult: ['yes', Validators.required],
    incidentDate: [{ value: '', disabled: false }, Validators.required],
    incidentTime: [{ value: '', disabled: false }, Validators.required],
    city: [{ value: '', disabled: false }, Validators.required],
    state: [{ value: null as string | null, disabled: false }, Validators.required],
    country: [{ value: null as string | null, disabled: false }, Validators.required]
  });
  constructor() {
  }

  ngOnInit(): void {
    this.setCountryAndStateData();
    this.subscription.add(this.dataExtractionService.incidentformData$
      .subscribe(incidentformData => {
        this.setupForm(incidentformData);
      }));
    this.incidentform.get('accidentResult')?.valueChanges.subscribe(value => {
      if (value === 'no') {
        this.disableOtherFields();
        this.allowNext = true;
      } else {
        this.enableOtherFields();
        this.allowNext = false;
      }
    });
    this.subscription.add(
      this.incidentform.statusChanges.subscribe(status => {
        // Allow Next only if form is valid OR auto-populated by checkbox
        this.allowNext = (status === 'VALID');
      })
    );
  }
  setCountryAndStateData() {
    forkJoin({
      countrydata: this.registerService.getRegisterPageFields().pipe(take(1)),
      stateData: this.registerService.getRegions('US').pipe(take(1))
    }).subscribe({
      next: ({ countrydata, stateData }) => {
        this.pageFieldData = countrydata;
        this.countriesWSList = this.pageFieldData.countriesWSList;
        this.stateList = stateData;
        this.timeout = setTimeout(() => {
          this.incidentform.get('country')?.setValue('US', { emitEvent: false });
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        console.error('Failed to load register fields/regions:', err);
      }
    });
  }
  onCountryChange(event: any, preSelectedState: string | null = null) {
    const raw = typeof event === 'string' ? event : event?.target?.value;
    const countryIso = raw ? (raw.includes(':') ? raw.split(':')[1].trim() : raw.trim()) : '';

    if (!countryIso) {
      this.stateList = null;
      return;
    }
    this.registerService.getRegions(countryIso).pipe(take(1)).subscribe((data) => {
      this.stateList = data;
      if (preSelectedState) {
        this.timeout = setTimeout(() => {
          this.incidentform.get('state')?.setValue(preSelectedState, { emitEvent: false });
        });
      }
    });
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }
  back() {
    this.dataExtractionService.setincidentformData(this.incidentform.value);
    this.dataExtractionService.currentComponent$.next('eCUInformation');
  }
  next() {
    const formatted = this.formatIncidentDate(this.incidentform.value.incidentDate);

    this.incidentform.patchValue({
      incidentDate: formatted
    });

    this.dataExtractionService.setincidentformData(this.incidentform.value);
    this.dataExtractionService.currentComponent$.next('returnShippingInformation');
  }

  formatIncidentDate(date: any) {
    return this.datePipe.transform(date, 'dd/MM/yyyy');
  }

  disableOtherFields() {
    const fields = ['incidentDate', 'incidentTime', 'city', 'country', 'state'];
    fields.forEach(f => {
      if (f === 'country' || f === 'state') {
        this.incidentform.controls[f].patchValue(null);
      }
      else {
        this.incidentform.controls[f].patchValue('');
      }
      this.incidentform.get(f)?.disable();
    });
  }

  enableOtherFields() {
    const fields = ['incidentDate', 'incidentTime', 'city', 'country', 'state'];
    fields.forEach(f => this.incidentform.get(f)?.enable());
    this.setCountryAndStateData();
  }
  setupForm(data: any) {
    const countryIso = data?.country;
    const stateIso = data?.state;

    const { state, ...rest } = data;
    this.incidentform.patchValue(rest);

    this.registerService
      .getRegions(countryIso)
      .pipe(take(1))
      .subscribe((stateData: any) => {
        this.stateList = stateData;

        const isValid = this.stateList?.regionsWSList?.some(
          (x: any) => x.isocode === stateIso
        );

        this.timeout = setTimeout(() => {
          this.incidentform
            .get('state')
            ?.setValue(isValid ? stateIso : null, { emitEvent: false });

          this.incidentform.updateValueAndValidity();
          this.allowNext = true;
        });
      });
  }
}
